import {
  Component,
  OnInit,
  Input,
  Inject,
  OnDestroy,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Page } from '@bloomreach/spa-sdk';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { AppStateService } from '@services/app-state.service';
import { WidenService } from '@services/widen.service';
import { FiltersService } from '@search/services/filters.service';
import { FtSearchService } from '@search/services/ftsearch.service';
import { ConfigService } from '@search/services/config.service';
import { SearchServiceToken } from '@frk/eds-components';
import { PromoCollection } from '@marketing/insights/insights.interface';
import { Link } from '@types';
import { FtSearchOptions } from '@search/interfaces/search.interface';
import { HttpParams } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LinkService } from '@services/link.service';

@Component({
  selector: 'ft-insights-sidebar',
  templateUrl: './insights-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightsSidebarComponent
  extends AbstractBaseComponent
  implements OnInit, OnChanges, OnDestroy {
  /**
   * Bloomreach document data
   */
  @Input() documentData: any;

  /**
   * Show hide top 4 promo card
   */
  @Input() enableTopFour: boolean;

  /**
   * Bloomreach page object
   */
  @Input() page: Page;

  /**
   * Translation object
   */
  @Input() translationLabel: any;

  /**
   * Object for promo card and collections
   */
  collectionData: PromoCollection;

  /**
   * Observable for component destroy
   */
  componentDestroyed$: Subject<boolean> = new Subject();

  /**
   * Article search api options
   */
  options: FtSearchOptions = {
    page: 0,
    term: '*',
    dataSource: 'dev',
    articleType: 'insight',
    current: '',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    // TODO verify if we use it and move to config
    counters: {
      funds: 4,
      literature: 4,
      pages: 4,
    },
  };

  /**
   * Constructor
   */
  constructor(
    private widenService: WidenService,
    private appStateService: AppStateService,
    @Inject(SearchServiceToken) private searchService: FtSearchService,
    private filters: FiltersService,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private linkService: LinkService
  ) {
    super();
  }

  /**
   * Ng On Changes
   */
  ngOnChanges(): void {
    this.collectionData = this.getCollectionData();
  }

  /**
   * Get promo card and collection data
   * from bloomreach document object
   */
  getCollectionData() {
    const content: PromoCollection = this.documentData;
    content.contentTop = {};
    content.contentBottom = {};
    if (content?.heading) {
      content.contentTop.title = content.heading;
    }
    if (content?.bodyCopy) {
      content.contentTop.summary = content.bodyCopy;
    }
    if (content?.link?.linkCollection?.length) {
      const headingLink = content.link.linkCollection[0];
      content.contentTop.linkUrl = this.linkService.getCTALink(
        this.page,
        headingLink,
        this.appStateService?.getHomePageUrl()
      );
      content.contentTop.linkText = headingLink?.displayText;
      content.contentTop.iconType = 'carrot-right';
    }

    if (content?.featuredHeading) {
      content.contentBottom.title = content.featuredHeading;
    }

    if (content?.featuredLinks?.length) {
      content.contentBottom.articles = [];
      content.featuredLinks.forEach((element: Link, index) => {
        const featuredLink = element.linkCollection[0];
        content.contentBottom.articles.push({
          indexKey: index + 1,
          description: featuredLink.displayText,
          href: this.linkService.getCTALink(
            this.page,
            featuredLink,
            this.appStateService?.getHomePageUrl()
          ),
        });
      });
    } else {
      if (this.enableTopFour) {
        content.contentBottom.articles = this.getDynamicFeaturedLinks(
          content.articleFilters,
          content.maxResults
        );
      }
    }

    if (content?.flyerCopy && content?.flyer) {
      content.contentBottom.summary = content.flyerCopy;
      content.contentBottom.linkText = this.translationLabel?.downloadFlayer
        ? this.translationLabel.downloadFlayer
        : 'Download Flyer';
      content.contentBottom.linkUrl = this.widenService.getWidenAssetUrl(
        content.flyer.widenDocument
      );
    }

    if (content?.collections?.length) {
      content.collections.forEach((element: Link, index) => {
        if (element.linkCollection?.length) {
          element = element.linkCollection[0];
          element.url = this.linkService.getCTALink(
            this.page,
            element,
            this.appStateService?.getHomePageUrl()
          );
          content.collections[index] = element;
        }
      });
    }
    return content;
  }

  /**
   * Get articles from api if
   * curated data is not available
   * @param selectedFilters object of selected filters
   */
  getDynamicFeaturedLinks(selectedFilters, maxResults: number) {
    const dynamicLinks = [];
    this.searchService.insightsTopFourOutput$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(({ results }) => {
        const dynamicResults = results.response?.hits?.hits;
        if (dynamicResults.length) {
          dynamicResults.forEach((element, index) => {
            let articleUrl;
            if (element._source?.relativePath?.startsWith('/')) {
              articleUrl =
                this.appStateService?.apiUrls?.spaBaseUrl +
                element._source?.relativePath;
            } else {
              articleUrl =
                this.appStateService?.apiUrls?.spaBaseUrl +
                '/' +
                element._source?.relativePath;
            }
            dynamicLinks.push({
              indexKey: index + 1,
              description: element._source.title,
              href: articleUrl,
            });
          });
        }
        this.cd.detectChanges();
      });
    this.configService
      .getConfig('insights')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((config) => {
        this.options.config = config;
        if (maxResults) {
          this.options.counters.pages = maxResults;
        }
        const searchParams = {
          options: this.options,
          filters: this.setPreFilter(selectedFilters),
        };
        this.searchService.getInsightsTopFour(searchParams);
      });
    return dynamicLinks;
  }

  /**
   * Set pre filters for search
   */
  setPreFilter(selectedFilters) {
    const filters = selectedFilters;
    const preFilters = [];
    if (filters) {
      const keys = [
        'assetclass',
        'investmentteam',
        'investmenttheme',
        'publicationseries',
        'speciality',
      ];
      Object.entries(filters).forEach(([key, value]) => {
        if (keys.includes(key.toLowerCase())) {
          if (value) {
            const arr = [];
            arr.push(value);
            if (key === 'assetClass' || key === 'assetclass') {
              preFilters.push({
                fieldName: 'assetclassPage.exact',
                fieldValue: arr,
              });
            } else {
              preFilters.push({
                fieldName: key.toLowerCase() + '.exact',
                fieldValue: arr,
              });
            }
          }
        }
      });
    }
    return JSON.stringify(preFilters);
  }

  /**
   * Ng On Destroy
   */
  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
